import {
	Stack,
	Paper,
	Box,
	Typography,
	Divider,
	Button,
} from '@mui/material'
import React, { useState } from 'react'
import { ScaffoldRequestForm } from '../../types/Scaffold/ScaffoldRequest'
import EstimationForm from './EstimationForm'
import { WorkItemTemplatesProvider } from '../WorkItemBuilderDialog/WorkItemTemplatesContext'
import CreateWorkItem from '../WorkItemBuilderDialog/CreateWorkItem'
import { ScaffoldWorkItem } from '../../types/Scaffold/Scaffold'
import WorkItemReadonlyTable from '../WorkItemBuilderDialog/WorkItemReadonlyTable'
import DecisionDialog from './DetailComponents/DecisionDialog'
import RequestInfoSection from './DetailComponents/RequestInfoSection'

interface RequestDetailsProps {
	scaffoldRequest: ScaffoldRequestForm
	setAssignDialogScaffold: (scaffold: ScaffoldRequestForm) => void
	assigneeId?: number
	refreshScaffolds: () => Promise<void>
	createWorkItems: (
		workItemData: ScaffoldWorkItem[],
		metadataValues: Record<number, string>,
		scaffoldRequestId: number,
	) => Promise<void>
}

export default function RequestDetails({
	scaffoldRequest,
	setAssignDialogScaffold,
	assigneeId,
	refreshScaffolds,
	createWorkItems,
}: RequestDetailsProps) {
	const [decisionDialogOpen, setDecisionDialogOpen] = useState(false)

	const handleOpenDecisionDialog = () => {
		setDecisionDialogOpen(true)
	}

	const handleCloseDecisionDialog = () => {
		setDecisionDialogOpen(false)
	}

	return (
		<Box sx={{ p: 2 }}>
			<Stack spacing={3} sx={{ maxWidth: '100%' }}>
				{/* Request Details Section with Attachments */}
				<RequestInfoSection
					scaffoldRequest={scaffoldRequest}
					onUpdateDecision={handleOpenDecisionDialog}
				/>

				{/* Estimation Form to be submitted */}
				{assigneeId && !scaffoldRequest.estimation && (
					<EstimationForm
						scaffoldRequestId={scaffoldRequest.id!}
						refreshData={refreshScaffolds}
						readOnly={false}
					/>
				)}

				{/* Estimation Form to be viewed */}
				{!assigneeId && scaffoldRequest.estimation && (
					<>
						<EstimationForm
							scaffoldRequestId={scaffoldRequest.id!}
							refreshData={refreshScaffolds}
							readOnly={true}
							initialEstimation={scaffoldRequest.estimation}
							initialExtraFields={scaffoldRequest.estimationExtraFields}
							estimatedBy={scaffoldRequest.estimateByFullname}
						/>

						{scaffoldRequest.workItems && scaffoldRequest.workItems.length > 0 ? (
							<Paper
								variant='outlined'
								sx={{
									p: 2,
									backgroundColor: '#f8f9fa',
									border: '1px solid #e0e0e0',
								}}
							>
								<Stack spacing={2}>
									<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
										<Typography variant='h6' sx={{ fontWeight: 600, color: 'primary.main' }}>
											Work Items
										</Typography>
									</Box>
									<Divider />
									<WorkItemReadonlyTable workItems={scaffoldRequest.workItems} />
								</Stack>
							</Paper>
						) : (
							<WorkItemTemplatesProvider scaffoldRequest={scaffoldRequest}>
								<CreateWorkItem
									onCreate={async (workItemData, metadataValues) =>
										await createWorkItems(workItemData, metadataValues, scaffoldRequest.id!)
									}
									refresh={refreshScaffolds}
									scaffoldRequest={scaffoldRequest}
								/>
							</WorkItemTemplatesProvider>
						)}
					</>
				)}

				{!assigneeId && !scaffoldRequest.estimation && (
					<Button
						variant='contained'
						color='secondary'
						onClick={() => {
							setAssignDialogScaffold(scaffoldRequest)
						}}
					>
						{scaffoldRequest.estimateById
							? 'Assigned to ' +
							  scaffoldRequest.estimateByFullname +
							  ' - ' +
							  scaffoldRequest.estimateByRole +
							  '. Reassign?'
							: 'Assign for Estimation'}
					</Button>
				)}
			</Stack>

			{/* Decision Update Dialog */}
			<DecisionDialog
				open={decisionDialogOpen}
				scaffoldRequest={scaffoldRequest}
				onClose={handleCloseDecisionDialog}
				refreshScaffolds={refreshScaffolds}
			/>
		</Box>
	)
}
